import { Component } from '@theme/component';
import { Wishlist, WishlistUpdateEvent } from '@theme/wishlist';

/**
 * A custom element that displays a wishlist icon with item count in the header.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wishlistBubble - The wishlist bubble element.
 * @property {HTMLElement} wishlistBubbleText - The wishlist bubble text element.
 * @property {HTMLElement} wishlistBubbleCount - The wishlist bubble count element.
 *
 * @extends {Component<Refs>}
 */
class WishlistIcon extends Component {
  requiredRefs = ['wishlistBubble', 'wishlistBubbleText', 'wishlistBubbleCount'];

  #initialized = false;

  /** @type {number} */
  get currentWishlistCount() {
    return parseInt(this.refs.wishlistBubbleCount.textContent ?? '0', 10);
  }

  set currentWishlistCount(value) {
    this.refs.wishlistBubbleCount.textContent = value < 100 ? String(value) : '99+';
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(WishlistUpdateEvent.eventName, this.onWishlistUpdate);

    // Check if parent section is already hydrated or will be hydrated
    const section = this.closest('[id^="shopify-section-"]');
    const isHydrated = section?.dataset.hydrated === 'true';

    if (isHydrated) {
      // Section already hydrated, initialize immediately
      this.#initializeWishlistState();
    } else {
      // Wait for hydration to complete (uses setTimeout as fallback if no hydration occurs)
      // Hydration sets data-hydrated="true", so we can observe for it
      const observer = new MutationObserver((mutations, obs) => {
        if (section?.dataset.hydrated === 'true') {
          obs.disconnect();
          this.#initializeWishlistState();
        }
      });

      if (section) {
        observer.observe(section, { attributes: true, attributeFilter: ['data-hydrated'] });
      }

      // Fallback: if no hydration after 1 second, initialize anyway
      setTimeout(() => {
        observer.disconnect();
        if (!this.#initialized) {
          this.#initializeWishlistState();
        }
      }, 1000);
    }
  }

  #initializeWishlistState() {
    if (this.#initialized) return;
    const count = Wishlist.getCount();
    if (count > 0) {
      this.renderWishlistBubble(count, false);
    }
    this.#initialized = true;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(WishlistUpdateEvent.eventName, this.onWishlistUpdate);
    this.#initialized = false;
  }

  /**
   * Called when section is re-rendered via Section Rendering API (e.g., header hydration).
   * Re-syncs display with localStorage since DOM was reset.
   */
  updatedCallback() {
    super.updatedCallback?.();
    // Re-apply wishlist state after section hydration resets the DOM
    const count = Wishlist.getCount();
    if (count > 0) {
      this.renderWishlistBubble(count, false);
    }
  }

  /**
   * Handles the wishlist update event.
   * @param {WishlistUpdateEvent} event - The wishlist update event.
   */
  onWishlistUpdate = (event) => {
    if (!this.#initialized) return;
    const itemCount = Wishlist.getCount();
    this.renderWishlistBubble(itemCount, true);
  };

  /**
   * Renders the wishlist bubble.
   * @param {number} itemCount - The number of items in the wishlist.
   * @param {boolean} animate - Whether to animate the bubble.
   */
  renderWishlistBubble(itemCount, animate = true) {
    this.refs.wishlistBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.wishlistBubble.classList.toggle('visually-hidden', itemCount === 0);
    this.refs.wishlistBubble.classList.toggle('wishlist-bubble--animating', itemCount > 0 && animate);

    this.currentWishlistCount = itemCount;
    this.classList.toggle('header-actions__wishlist-icon--has-items', itemCount > 0);

    if (animate && itemCount > 0) {
      setTimeout(() => {
        this.refs.wishlistBubble.classList.remove('wishlist-bubble--animating');
      }, 300);
    }
  }
}

if (!customElements.get('wishlist-icon')) {
  customElements.define('wishlist-icon', WishlistIcon);
}
